import Dto from '~/base/Dto';
import { modelFactory } from '~/base/ModelFactory';
import ApiHelper from '~/base/api/ApiHelper';
import HookFormHelper from '~helpers/HookFormHelper';
import { DealerCommentItemDto } from '~modules/surveys/dto/DealerCommentItemDto';
import { GetSurveyDto } from '~modules/surveys/dto/GetSurveyDto';
import { GetSurveyResultsDto } from '~modules/surveys/dto/GetSurveyResultsDto';
import { SendSurveyDto } from '~modules/surveys/dto/SendSurveyDto';
import { SurveyCommentsDto } from '~modules/surveys/dto/SurveyCommentsDto';
import { SurveyResultDto } from '~modules/surveys/dto/SurveyResultDto';
import { SurveyForm, SurveyFormField } from '~modules/surveys/forms/SurveyForm';
import { SurveyResultCommentForm } from '~modules/surveys/forms/SurveyResultCommentForm';
import { SurveyExtraQuestionModel } from '~modules/surveys/models/SurveyExtraQuestionModel';
import { SurveyModel } from '~modules/surveys/models/SurveyModel';
import { SurveyQuestionModel } from '~modules/surveys/models/SurveyQuestionModel';
import { SurveyResultCommentFormModel } from '~modules/surveys/models/SurveyResultCommentFormModel';
import { SurveyResultsModel } from '~modules/surveys/models/SurveyResultsModel';
import { IBaseSurveyProps } from '~modules/surveys/types/IBaseSurveyProps';
import { SurveyAnswerTypeIdEnum } from '~modules/surveys/types/SurveyAnswerTypeIdEnum';

import SurveyApiRepository from './SurveyApiRepository';

export default class SurveyService {
  api: SurveyApiRepository;

  constructor() {
    this.api = new SurveyApiRepository();
  }

  // API

  getSurvey = async (props: IBaseSurveyProps) => {
    const dto = Dto.populate(GetSurveyDto, props);

    const params = ApiHelper.getURLSearchParams(ApiHelper.getTrimStringValues({ ...dto }));

    const { data } = await this.api.getSurvey(params);

    return modelFactory.create(SurveyModel, data.data);
  };

  sendSurvey = async (form: SurveyForm, survey: SurveyModel, { carId, workOrderId }: IBaseSurveyProps) => {
    const results = this.prepareResults(form);

    const dto = Dto.populate(SendSurveyDto, {
      formId: survey.formId,
      formTypeId: survey.formTypeId,
      carId: Number(carId) || null,
      workOrderId: Number(workOrderId) || null,
      results,
    });

    return await this.api.sendSurvey(dto);
  };

  getSurveyResults = async (props: IBaseSurveyProps) => {
    const dto = Dto.populate(GetSurveyResultsDto, props);

    const params = ApiHelper.getURLSearchParams(ApiHelper.getTrimStringValues({ ...dto }));

    const { data } = await this.api.getSurveyResults(params);

    return modelFactory.create(SurveyResultsModel, data.data);
  };

  closeSurvey = async (form: SurveyResultCommentForm, props: IBaseSurveyProps) => {
    const data: DealerCommentItemDto[] = Object.entries(form.comments).map(([questionId, dealerNotes]) =>
      modelFactory.create(DealerCommentItemDto, {
        questionId: Number(questionId),
        dealerNotes,
      }),
    );

    const dto = Dto.populate(SurveyCommentsDto, {
      workorderId: props.workOrderId,
      data,
    });

    return await this.api.closeSurvey(dto);
  };

  saveCommentsFromDealer = async (form: SurveyResultCommentForm, props: IBaseSurveyProps) => {
    const data: DealerCommentItemDto[] = Object.entries(form.comments).map(([questionId, dealerNotes]) =>
      modelFactory.create(DealerCommentItemDto, {
        questionId: Number(questionId),
        dealerNotes,
      }),
    );

    const dto = Dto.populate(SurveyCommentsDto, {
      workorderId: props.workOrderId,
      data,
    });

    return await this.api.saveCommentsFromDealer(dto);
  };

  // OTHERS

  // Устанавливаем по дефолту первый вопрос, остальные вопросы скрыты
  prepareSurveyFormDefaultValue = (data: SurveyQuestionModel[]) => {
    return { [SurveyFormField.questions]: data[0] ? [HookFormHelper.prepareData(data[0])] : [] };
  };

  prepareSurveyFormField = (data: SurveyQuestionModel) => {
    return HookFormHelper.prepareData(data);
  };

  prepareDefaultExtraQuestion = (data?: SurveyExtraQuestionModel) => {
    return HookFormHelper.prepareData(data);
  };

  prepareResults = (form: SurveyForm) => {
    const results: SurveyResultDto[] = [];

    const parseQuestion = (question: SurveyQuestionModel | SurveyExtraQuestionModel) => {
      switch (question.answerTypeId) {
        case SurveyAnswerTypeIdEnum.RADIO_GROUP:
          results.push(
            modelFactory.create(SurveyResultDto, {
              questionId: question.questionId,
              answerId: question?.answerId ?? null,
              happyIndex: question?.happyIndex ?? null,
              textValue: question?.textValue ?? null,
            }),
          );
          break;

        case SurveyAnswerTypeIdEnum.CHECKBOX_GROUP:
          question.answers.forEach((answer) => {
            if (answer.isChecked) {
              results.push(
                modelFactory.create(SurveyResultDto, {
                  questionId: answer.questionId,
                  answerId: answer.answerId ?? null,
                  happyIndex: null,
                  textValue: answer.textValue ?? null,
                }),
              );
            }
          });
          break;

        case SurveyAnswerTypeIdEnum.TEXT_SHORT:
        case SurveyAnswerTypeIdEnum.TEXT_LONG:
          results.push(
            modelFactory.create(SurveyResultDto, {
              questionId: question.questionId,
              answerId: null,
              happyIndex: null,
              textValue: question?.textValue ?? null,
            }),
          );
          break;

        case SurveyAnswerTypeIdEnum.HAPPY_INDEX_5:
        case SurveyAnswerTypeIdEnum.HAPPY_INDEX_10:
          results.push(
            modelFactory.create(SurveyResultDto, {
              questionId: question.questionId,
              answerId: null,
              happyIndex: question?.happyIndex ?? null,
              textValue: null,
            }),
          );
          break;
      }

      if (!('extraQuestions' in question)) {
        return;
      }

      question.extraQuestions.forEach(parseQuestion);
    };

    form.questions.forEach(parseQuestion);

    return results;
  };

  prepareSurveyResultCommentFormValues = (data: SurveyResultsModel) => {
    const comments: Record<number, string> = {};

    data.results.forEach((result) => {
      if (result.questionId && result.isNeedComment && result.dealerComment) {
        comments[result.questionId] = result.dealerComment;
      }
    });

    return modelFactory.create(SurveyResultCommentFormModel, {
      comments,
    });
  };
}
